import * as React from 'react';

import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { classes } from '~utils/Classes';

import {
    IShowcaseClickableItem,
    IShowcaseItem,
    IShowcaseSlidersProps,
    IShowcaseTeaser
} from './IShowcase';
import { ClickableShowcaseImage, EditorialLink, StyledSlider } from './ShowcaseWrapper';
import { sliderImageSettings, sliderTextSettings } from './sliderSettings';

const slideImageClass = classes('account-showcase-image');
const slideLinkClass = classes('account-showcase-link');
const slideTitleClass = classes('account-showcase-title');
const slideTeaserClass = classes('account-showcase-teaser');

const sliderImageClass = classes('account-showcase-image-slider');
const sliderCopyClass = classes('account-showcase-copy-slider');

const slideSelector = '.elc-account-showcase .slick-slide';
const activeSlideSelector = 'slick-current';

export const TEST_ID_SHOWCASE_IMAGE_SLIDER = 'account-showcase-image-slider';
export const TEST_ID_SHOWCASE_COPY_SLIDER = 'account-showcase-copy-slider';

const ShowcaseTeaser = observer(function ShowcaseTeaser({ title, teaser }: IShowcaseTeaser) {
    return (
        <>
            {title && <h3 className={slideTitleClass}>{title}</h3>}
            {teaser && <span className={slideTeaserClass}>{teaser}</span>}
        </>
    );
});

const ShowcaseImageItem = observer(function ShowcaseItem({
    item,
    onClick
}: IShowcaseClickableItem) {
    const { img, url = '' } = item;
    const handleOnClick = () => onClick(item);
    const srcMobile = img.srcMobile ? img.srcMobile : img.src;

    return (
        <>
            <ClickableShowcaseImage href={url} onClick={handleOnClick}>
                <img
                    className={slideImageClass}
                    alt={img.alt}
                    srcSet={`${srcMobile} 206w, ${img.src} 274w`}
                    sizes="(max-width: 380px) 206px, 274px"
                    src={srcMobile}
                ></img>
            </ClickableShowcaseImage>
        </>
    );
});

const ShowcaseCopyItem = observer(function ShowcaseItem({ item, onClick }: IShowcaseClickableItem) {
    const { label = '', url = '', showTeaser = true } = item;
    const handleOnClick = () => onClick(item);

    return (
        <>
            {showTeaser && <ShowcaseTeaser title={item?.title} teaser={item?.teaser} />}
            <EditorialLink className={slideLinkClass} href={url} onClick={handleOnClick}>
                {label}
            </EditorialLink>
        </>
    );
});

@observer
export class ShowcaseSliders extends React.Component<IShowcaseSlidersProps> {
    public static displayName: string;

    @observable private imageNav: HTMLDivElement | null;
    @observable private copyNav: HTMLDivElement | null;

    private imageRef: React.RefObject<HTMLDivElement> = React.createRef();
    private copyRef: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount() {
        this.imageNav = this.imageRef?.current;
        this.copyNav = this.copyRef?.current;

        this.setTabIndexSlickSlides();
        document.addEventListener('keydown', this.setTabIndexSlickSlides);
    }

    componentWillUnmount = () => {
        document.removeEventListener('keydown', this.setTabIndexSlickSlides);
    };

    setTabIndexSlickSlides() {
        Array.from(document.querySelectorAll(slideSelector)).forEach((slide: HTMLElement) => {
            const isActiveSlide = slide.classList.contains(activeSlideSelector);
            const tabIndex = isActiveSlide ? '0' : '-1';
            const link = slide.getElementsByTagName('a');

            link[0]?.setAttribute('tabindex', tabIndex);
        });
    }

    render() {
        const { list, onClick } = this.props;
        const sliderImageItems = list?.map((item: IShowcaseItem) => (
            <ShowcaseImageItem key={`${item.showCaseKey}-image`} item={item} onClick={onClick} />
        ));

        const sliderCopyItems = list?.map((item: IShowcaseItem) => (
            <ShowcaseCopyItem key={`${item.showCaseKey}-copy`} item={item} onClick={onClick} />
        ));

        return (
            <>
                <StyledSlider
                    className={sliderImageClass}
                    dataTestId={TEST_ID_SHOWCASE_IMAGE_SLIDER}
                    sliderSettings={sliderImageSettings}
                    items={sliderImageItems}
                    innerRef={this.imageRef}
                    asNavFor={this.copyNav}
                />
                <StyledSlider
                    className={sliderCopyClass}
                    dataTestId={TEST_ID_SHOWCASE_COPY_SLIDER}
                    sliderSettings={sliderTextSettings}
                    items={sliderCopyItems}
                    innerRef={this.copyRef}
                    asNavFor={this.imageNav}
                />
            </>
        );
    }
}
